import React, { useState } from 'react';
import { Link } from 'react-router';
import Sidebar from './components/Sidebar';
import EndpointCard from './components/EndpointCard';
import endpoints from './data/endpoints';
import './docsScreen.css';

function DocsScreen() {
  const [activeCategory, setActiveCategory] = useState('health');
  const [activeEndpoint, setActiveEndpoint] = useState('health-check');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);

  const handleSelectEndpoint = (category, endpointId) => {
    setActiveCategory(category);
    setActiveEndpoint(endpointId);
    setIsSidebarOpen(false); // Close mobile sidebar after selection
  };

  const getCurrentEndpoint = () => {
    if (!endpoints[activeCategory]) return null;
    return endpoints[activeCategory].endpoints.find(e => e.id === activeEndpoint);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      {/* Simple Header with Logo */}
      <div className={`${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} border-b px-4 py-3`}>
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="/uro_logo_white.png"
              alt="Uro Logo"
              className="h-8 select-none"
              draggable={false}
              style={{ filter: isDarkMode ? 'brightness(1)' : 'brightness(0.2)' }}
            />
          </Link>
          <h1 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            API Documentation
          </h1>
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`p-2 rounded-md transition-colors ${
              isDarkMode 
                ? 'bg-slate-700 hover:bg-slate-600 text-amber-400' 
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
            }`}
          >
            {isDarkMode ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 7a5 5 0 100 10 5 5 0 000-10zM2 13h2a1 1 0 100-2H2a1 1 0 100 2zm18 0h2a1 1 0 100-2h-2a1 1 0 100 2zM11 2v2a1 1 0 102 0V2a1 1 0 10-2 0zm0 18v2a1 1 0 102 0v-2a1 1 0 10-2 0zM5.99 4.58a1 1 0 10-1.41 1.41l1.06 1.06a1 1 0 101.41-1.41L5.99 4.58zm12.37 12.37a1 1 0 10-1.41 1.41l1.06 1.06a1 1 0 101.41-1.41l-1.06-1.06zm1.06-10.96a1 1 0 10-1.41-1.41l-1.06 1.06a1 1 0 101.41 1.41l1.06-1.06zM7.05 18.36a1 1 0 10-1.41-1.41l-1.06 1.06a1 1 0 101.41 1.41l1.06-1.06z"/>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21.64 13a1 1 0 00-1.05-.14 8.05 8.05 0 01-3.37.73 8.15 8.15 0 01-8.14-8.1 8.59 8.59 0 01.25-2A1 1 0 008 2.36a10.14 10.14 0 1014 11.69 1 1 0 00-.36-1.05z"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className={`lg:hidden fixed top-16 left-4 z-50 p-2 rounded-md shadow-lg border ${
          isDarkMode 
            ? 'bg-slate-800 border-slate-600 text-slate-100' 
            : 'bg-white border-gray-300 text-gray-900'
        }`}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {isSidebarOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      <div className="flex h-[calc(100vh-4rem)]">
        {/* Sidebar - Desktop */}
        <div className={`hidden lg:flex flex-col w-80 border-r shadow-sm ${
          isDarkMode 
            ? 'bg-slate-800 border-slate-700' 
            : 'bg-white border-gray-200'
        }`}>
          <div className={`flex-1 overflow-y-auto ${isDarkMode ? 'docs-scrollbar-dark' : 'docs-scrollbar'}`}>
            <Sidebar
              categories={endpoints}
              activeCategory={activeCategory}
              activeEndpoint={activeEndpoint}
              onSelectEndpoint={handleSelectEndpoint}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>

        {/* Sidebar - Mobile */}
        <div className={`lg:hidden fixed inset-0 z-40 pt-16 ${isSidebarOpen ? 'block' : 'hidden'}`}>
          <div className="fixed inset-0 bg-black opacity-50" onClick={() => setIsSidebarOpen(false)} />
          <div className={`fixed left-0 top-16 h-[calc(100vh-4rem)] w-80 shadow-lg ${
            isDarkMode ? 'bg-slate-800' : 'bg-white'
          }`}>
            <div className={`h-full overflow-y-auto ${isDarkMode ? 'docs-scrollbar-dark' : 'docs-scrollbar'}`}>
              <Sidebar
                categories={endpoints}
                activeCategory={activeCategory}
                activeEndpoint={activeEndpoint}
                onSelectEndpoint={handleSelectEndpoint}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-1 lg:ml-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
          <div className={`h-full overflow-y-auto ${isDarkMode ? 'docs-scrollbar-dark' : 'docs-scrollbar'}`}>
            <EndpointCard endpoint={getCurrentEndpoint()} isDarkMode={isDarkMode} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocsScreen;